import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Menu, MenuItem, Paper, Skeleton, SpeedDial, SpeedDialAction, Stack, TextField, Typography, Drawer, FormControl, InputLabel, Select, OutlinedInput, Checkbox, ListItemText, Divider, DialogContentText, List, ListItem, Popover, Breadcrumbs, Tooltip, Tab, Tabs, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { memo, useEffect, useMemo, useState } from "react";
import { Add, Cancel, Menu as MenuIcon, Edit, Search, FilterList, Clear, CloudUpload, NoteAlt, Movie, InsertDriveFile, Description, StickyNote2, VideoLibrary, NavigateNext, Info, School, Collections, Settings as SettingsIcon, CheckCircleOutline, Close, Public, Unpublished, ArrowBack, Publish } from "@mui/icons-material";
import BackdropLoader from "../../../../components/backdrop-loader";
import CollectionDetailsDrawer from "../components/collection-details-drawer";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import ResourcesSection from "../components/resources-section";
import { useDomainActions } from "../../../../hooks/useDomainActions";
import { CollectionStatus } from "../../../../models/collection";
import { useSnackbar } from "../../../../components/snackbar";
import isHubActivated from "../../../../utils/is-hub-activated";
import PublishCollectionDialog from "../components/publish-collection-dialog";

type RouteParams = {
    classroom_id: string;
    collection_id: string;
};


function CollectionDetails() {
    const { classroom_id, collection_id } = useParams<RouteParams>();

    const [drawerOpen, setDrawerOpen] = useState(false);

    
    const { 
        classrooms, 
        getClassrooms, 
        getCollections, 
        updateCollectionStatus,
        getResources
    } = useClassroomActions();

    const { hub, getHub } = useDomainActions();
    
    const classroom = classrooms?.[Number(classroom_id)];

    const collections = classroom?.collections;

    const collection = collections?.[Number(collection_id)];
  
    const [loading, setLoading] = useState(true);

    const [toggling, setToggling] = useState(false);
    
    const [firstPublishDialog, setFirstPublishDialog] = useState(false);
    
    const { showMessage } = useSnackbar();


    const handleToggleCollectionClick = () => {
        if (!collection) return;

        if (collection.status === CollectionStatus.DRAFT) {
            setFirstPublishDialog(true);
        }
        else {
            toggleCollectionStatus();
        }      
    };

    const toggleCollectionStatus = async () => {
        if (!collection) return;

        setToggling(true);

        const newStatus = collection.status === CollectionStatus.PUBLISHED ? CollectionStatus.ARCHIVED : CollectionStatus.PUBLISHED;

        if (newStatus === CollectionStatus.PUBLISHED && !isHubActivated(hub)) {
            showMessage('Hub is not activated', 1);
            return;
        }
        
        await updateCollectionStatus(Number(classroom_id), collection.id, newStatus);
        
        setToggling(false);

        showMessage(newStatus === CollectionStatus.PUBLISHED ? "Collection Published" : "Collection Archived", 3);
    };


    useEffect(() => {
        const loadNeededData = async () => {
            if (!hub) await getHub();

            if (!classrooms) await getClassrooms();

            if (classroom && !collections) await getCollections(Number(classroom_id));

            if (collection) {
                if (!collection.resources) await getResources(Number(classroom_id), Number(collection_id));
                setLoading(false);
            };

            setLoading(false);
        };

        loadNeededData();

    }, [classrooms, classroom_id, collection_id]);
    
    
    if (!hub || !classrooms || !classroom || !collection || !collection.resources || loading) {
        return (
            <BackdropLoader/>
        );
    }

  

    return (
        <Box sx={{ backgroundColor: 'grey.100', minHeight: '100vh' }}>

            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: { xs: 1.5, sm: 2 },
                    backgroundColor: "#1e293b",
                    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                    backdropFilter: 'blur(8px)',
                    position: 'relative',
                }}
            >
                <Box
                    component={Link}
                    to={`/classroom/${classroom_id}#library`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        textDecoration: 'none',
                        color: '#475569',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        minWidth: 0, // Allow text to truncate
                        flex: 1,
                    }}
                >
                    <ArrowBack 
                        fontSize="small" 
                        sx={{ 
                            flexShrink: 0,
                            color: '#64748b',
                            transition: 'color 0.2s ease',
                        }} 
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                color: '#64748b',
                                lineHeight: 1,
                                mb: 0.25,
                            }}
                        >
                            Collections
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: '#f1f5f9',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {collection && collection.title || "Untitled Collection"}
                        </Typography>
                    </Box>
                </Box>
                
                <Stack spacing={1} direction="row">
                    <Button
                        size="medium"
                        variant="contained"
                        disabled={toggling}
                        startIcon={
                            toggling ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                    ? <Publish fontSize="small" />
                                    : <Unpublished fontSize="small" />
                            )
                        }
                        onClick={handleToggleCollectionClick}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px',
                            px: 3,
                            py: 1.25,
                            minWidth: 140,
                            fontSize: '0.9rem',
                            letterSpacing: 0.3,
                            background: (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                ? 'linear-gradient(145deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.25))'
                                : 'linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.25))',
                            color: (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                ? '#4ade80'  // Light green text
                                : '#f87171', // Light red text
                            border: (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                ? '1px solid rgba(34, 197, 94, 0.3)'
                                : '1px solid rgba(239, 68, 68, 0.3)',
                            boxShadow: 'none',
                            backdropFilter: 'blur(8px)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                background: (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                    ? 'linear-gradient(145deg, rgba(34, 197, 94, 0.25), rgba(22, 163, 74, 0.35))'
                                    : 'linear-gradient(145deg, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.35))',
                                color: (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                    ? '#34d399'
                                    : '#fb7185',
                                borderColor: (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                    ? 'rgba(34, 197, 94, 0.5)'
                                    : 'rgba(239, 68, 68, 0.5)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                            '&:disabled': {
                                background: 'rgba(51, 65, 85, 0.5)',
                                color: '#94a3b8',
                                border: '1px solid rgba(148, 163, 184, 0.2)',
                            },
                        }}
                    >
                        {toggling
                            ? 'Processing...'
                            : (collection.status === CollectionStatus.DRAFT || collection.status === CollectionStatus.ARCHIVED)
                                ? 'Publish'
                                : 'Unpublish'}
                    </Button>


                    <IconButton
                        sx={{
                            padding: '8px',
                            color: '#64748b',
                            backgroundColor: 'rgba(148, 163, 184, 0.1)',
                            border: '1px solid rgba(148, 163, 184, 0.2)',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                color: '#3b82f6',
                                borderColor: 'rgba(59, 130, 246, 0.3)',
                                transform: 'translateY(-1px)',
                            },
                        }}
                        onClick={() => {
                            setDrawerOpen(true);
                        }}
                    >
                        <MenuIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>
            
            
             <ResourcesSection 
                classroom_id={classroom_id!} 
                collection_id={collection_id!} 
                collection={collection}
                hub={hub}
            />

            <CollectionDetailsDrawer
                classroom_id={classroom_id!}
                collection={collection}
                open={drawerOpen}
                setOpen={setDrawerOpen}
                hub={hub}
            />


            <PublishCollectionDialog
                open={firstPublishDialog}
                setOpen={setFirstPublishDialog}
                updateCollectionDetails={toggleCollectionStatus}
            />
        </Box>
    );
}

export default (CollectionDetails);